// Tile Renderer - Konva-based rendering of hex tiles
import Konva from 'konva';
import { CONFIG, buildTileUrl, getTileMetadata } from '../config.js';
import { HexMath } from '../utils/hex-math.js';
import { getCurrentRevealState } from '../main.js';

/**
 * TileRenderer handles composing a hex map from individual tile images
 * using Konva.js for efficient canvas rendering.
 */
class TileRenderer {
    constructor() {
        // Tile image cache (in-memory)
        this.tileCache = new Map();

        // Tile dimensions (will be detected from first loaded tile)
        // Hexton Hills tiles are 270x234 pixels, already flat-top orientation
        this.tileWidth = 270;
        this.tileHeight = 234;

        // Hex size (circumradius) derived from tile dimensions
        // For flat-top hex: width = 2R, so R = width / 2
        this.hexSize = null; // Will be calculated from first tile
    }

    /**
     * Load a tile image, using cache if available
     *
     * @param {string} tileId - The tile identifier (e.g., "AA01")
     * @returns {Promise<HTMLImageElement>}
     */
    async loadTile(tileId) {
        // Check cache first
        if (this.tileCache.has(tileId)) {
            return this.tileCache.get(tileId);
        }

        // Load the tile image
        const url = buildTileUrl(tileId);

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                // Update tile dimensions and calculate hexSize from first tile
                if (this.tileCache.size === 0) {
                    this.tileWidth = img.naturalWidth;
                    this.tileHeight = img.naturalHeight;
                    // Tiles are flat-top where width = 2 * circumradius
                    this.hexSize = img.naturalWidth / 2;
                }
                this.tileCache.set(tileId, img);
                resolve(img);
            };
            img.onerror = () => {
                console.warn(`Failed to load tile: ${tileId}`);
                reject(new Error(`Failed to load tile: ${tileId}`));
            };
            img.src = url;
        });
    }

    /**
     * Preload all tiles needed for a map
     *
     * @param {Object} mapData - Map data with tiles array
     * @param {Function} onProgress - Progress callback (loaded, total)
     * @returns {Promise<void>}
     */
    async preloadTiles(mapData, onProgress = null) {
        // Get unique tile IDs
        const tileIds = [...new Set(mapData.tiles.map(t => t.tileId))];
        let loaded = 0;

        // Load in parallel batches
        const batchSize = 20;
        for (let i = 0; i < tileIds.length; i += batchSize) {
            const batch = tileIds.slice(i, i + batchSize);
            await Promise.all(batch.map(async (tileId) => {
                try {
                    await this.loadTile(tileId);
                    loaded++;
                    if (onProgress) {
                        onProgress(loaded, tileIds.length);
                    }
                } catch (e) {
                    // Continue even if some tiles fail
                    loaded++;
                    if (onProgress) {
                        onProgress(loaded, tileIds.length);
                    }
                }
            }));
        }
    }

    /**
     * Calculate canvas dimensions needed to render the map
     * Uses shared HexMath for positioning calculations
     */
    calculateCanvasSize(mapData, hexSize) {
        // Calculate actual bounds from tiles (don't trust JSON bounds - they often have extra padding)
        const actualBounds = HexMath.calculateActualBounds(mapData.tiles);

        // Minimal padding to prevent edge clipping - just 2 pixels
        const padding = 2;
        return HexMath.calculateCanvasSize(actualBounds, hexSize, padding);
    }

    /**
     * Render a single tile as a Konva.Image
     *
     * @param {HTMLImageElement} img - Tile image
     * @param {string} tileId - Tile identifier (e.g., "AL07")
     * @param {number} x - Center X position
     * @param {number} y - Center Y position
     * @param {number} rotation - Rotation in degrees (from map data)
     * @returns {Konva.Image}
     */
    createTileImage(img, tileId, x, y, rotation) {
        // Hexton Hills rotation interpretation:
        // rot=30 is the base/default for flat-top display
        // Apply (rot - base) to get effective rotation while keeping tiles flat-top
        const baseRotation = CONFIG.HEXTON_ROTATION_BASE;
        const effectiveRotation = (rotation ?? baseRotation) - baseRotation;

        // Get tile metadata to check for special rendering requirements
        const metadata = getTileMetadata(tileId);

        // Calculate rotation center offset
        // For tiles with bottom bleed (e.g., AL07 castle), use standardHeight to ensure
        // rotation center aligns with the hex grid, not the extended image bounds
        const offsetX = img.naturalWidth / 2;
        const offsetY = metadata.standardHeight
            ? metadata.standardHeight / 2
            : img.naturalHeight / 2;

        // Create Konva.Image with proper offset for centering
        const konvaImage = new Konva.Image({
            image: img,
            x: x,
            y: y,
            width: img.naturalWidth,
            height: img.naturalHeight,
            offsetX: offsetX,
            offsetY: offsetY,
            rotation: effectiveRotation,
            listening: false,
            perfectDrawEnabled: false,
            // Explicitly set full opacity
            opacity: 1,
            // Prevent any alpha compositing issues
            globalCompositeOperation: 'source-over',
            // Disable hit detection
            hitStrokeWidth: 0
        });

        return konvaImage;
    }

    /**
     * Render all tiles to a Konva Layer
     *
     * @param {Konva.Layer} layer - The layer to add tiles to
     * @param {Object} mapData - Map data with tiles, bounds, etc.
     * @param {Function} onProgress - Progress callback
     * @param {Object} options - Rendering options
     * @param {Object} options.session - Session with hexStates for fog of war
     * @param {string} options.viewMode - 'gm' or 'player'
     * @returns {Promise<Object>} Dimensions info
     */
    async renderToLayer(layer, mapData, onProgress = null, options = {}) {
        const { session = null, viewMode = 'gm' } = options;
        // Preload all tiles first (this also calculates hexSize from first tile)
        if (onProgress) {
            onProgress('preloading', 0, mapData.tiles.length);
        }
        await this.preloadTiles(mapData, (loaded, total) => {
            if (onProgress) {
                onProgress('preloading', loaded, total);
            }
        });

        // Use hex size calculated from actual tile dimensions
        const hexSize = this.hexSize || CONFIG.HEX_SIZE;

        // Calculate canvas dimensions
        const dimensions = this.calculateCanvasSize(mapData, hexSize);

        // Sort tiles for proper draw order:
        // - Tiles with bottom bleed (e.g., AL07 castle) always render LAST (on top)
        // - Other tiles render in y,x order (top to bottom, left to right)
        // This ensures content bleed from special tiles appears above adjacent tiles
        const tiles = [...mapData.tiles].sort((a, b) => {
            const aHasBleed = getTileMetadata(a.tileId).hasBottomBleed;
            const bHasBleed = getTileMetadata(b.tileId).hasBottomBleed;

            // Tiles with bottom bleed always render after non-bleed tiles
            if (aHasBleed && !bHasBleed) return 1;
            if (!aHasBleed && bHasBleed) return -1;

            // Within same bleed category, sort by y (top to bottom), then x (left to right)
            if (a.pos.y !== b.pos.y) return a.pos.y - b.pos.y;
            return a.pos.x - b.pos.x;
        });

        // Add each tile as a Konva.Image
        let renderedCount = 0;
        for (let i = 0; i < tiles.length; i++) {
            const tile = tiles[i];
            const hexCoord = `${tile.pos.x},${tile.pos.y}`;
            const hexState = session?.hexStates?.[hexCoord];
            const isRevealed = hexState ? getCurrentRevealState(hexState) : false;

            // Player view: skip hidden tiles entirely
            if (viewMode === 'player' && !isRevealed) {
                continue;
            }

            try {
                const img = await this.loadTile(tile.tileId);

                // Calculate pixel position using shared HexMath
                const pos = HexMath.hexToPixel(
                    tile.pos.x,
                    tile.pos.y,
                    hexSize,
                    dimensions.offsetX,
                    dimensions.offsetY
                );

                // Create and add the tile image
                // Use tile.rot if defined, otherwise use base rotation (30)
                const tileRotation = tile.rot !== undefined ? tile.rot : CONFIG.HEXTON_ROTATION_BASE;
                const konvaImage = this.createTileImage(img, tile.tileId, pos.x, pos.y, tileRotation);

                // Store hex coordinate for later lookup (use name for Konva's findOne)
                konvaImage.name(HexMath.formatHexNameForSelector(hexCoord));

                // Player view: hide unrevealed tiles completely (prevents bleeding content from showing)
                if (viewMode === 'player' && !isRevealed) {
                    konvaImage.visible(false);
                }

                // GM view: dim unrevealed tiles (allows GM to see bleeding content for planning)
                if (viewMode === 'gm' && !isRevealed) {
                    konvaImage.opacity(CONFIG.FOG_UNREVEALED_OPACITY);
                }

                layer.add(konvaImage);
                renderedCount++;

                if (onProgress) {
                    onProgress('rendering', i + 1, tiles.length);
                }
            } catch (e) {
                console.warn(`Skipping tile ${tile.tileId}: ${e.message}`);
            }
        }

        // Batch draw for better performance
        layer.batchDraw();

        return {
            width: dimensions.width,
            height: dimensions.height,
            hexSize,
            offsetX: dimensions.offsetX,
            offsetY: dimensions.offsetY,
            bounds: mapData.bounds
        };
    }

    /**
     * Clear the tile cache
     */
    clearCache() {
        this.tileCache.clear();
    }
}

// Export singleton instance
export const tileRenderer = new TileRenderer();
export default TileRenderer;
